import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight, Quote, Play, X, Volume2, VolumeX } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getProjectById, projects } from '../data/portfolio';

// Video Player Modal
const VideoModal = ({ isOpen, onClose, vimeoId, title }) => {
  const [isMuted, setIsMuted] = useState(false);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95"
        onClick={onClose}
      >
        {/* Close button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          onClick={onClose}
          className="absolute top-6 right-6 z-50 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </motion.button>

        {/* Sound toggle */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          onClick={(e) => {
            e.stopPropagation();
            setIsMuted(!isMuted);
          }}
          className="absolute top-6 right-24 z-50 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          {isMuted ? (
            <VolumeX className="w-6 h-6 text-white" />
          ) : (
            <Volume2 className="w-6 h-6 text-white" />
          )}
        </motion.button>

        {/* Video */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 flex items-center justify-center p-8"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&loop=1&muted=${isMuted ? 1 : 0}&quality=1080p`}
            className="w-full h-full max-w-6xl rounded-2xl"
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
            title={title}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const ProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const project = getProjectById(projectId);
  const [showVideo, setShowVideo] = useState(false);

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold font-['Syne'] mb-4">Project Not Found</h1>
          <Link to="/work" className="btn-primary">Back to Work</Link>
        </div>
      </div>
    );
  }

  const currentIndex = projects.findIndex(p => p.id === projectId);
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const nextProject = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white text-black">
      <SEO 
        title={`${project.title} - Case Study`}
        description={project.shortDescription}
        url={`/work/${project.id}`}
        type="article"
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section with Video Option */}
        <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
          <motion.div
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <img
              src={project.heroImage}
              alt={project.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          </motion.div>

          {/* Play button for video projects */}
          {project.vimeoId && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              onClick={() => setShowVideo(true)}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 group"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-28 h-28 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:bg-white/20 transition-all"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-full border border-white/20"
                />
                <Play className="w-10 h-10 text-white fill-white ml-1" />
              </motion.div>
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="block text-white text-sm font-medium mt-4 text-center"
              >
                Watch Video
              </motion.span>
            </motion.button>
          )}
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <p className="text-white/70 text-sm uppercase tracking-widest mb-4">
                {project.categoryLabel} • {project.year}
              </p>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] text-white tracking-tight">
                {project.title}
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Video Modal */}
        {project.vimeoId && (
          <VideoModal
            isOpen={showVideo}
            onClose={() => setShowVideo(false)}
            vimeoId={project.vimeoId}
            title={project.title}
          />
        )}

        {/* Project Info */}
        <section className="section-padding border-b border-gray-100">
          <div className="container-custom">
            <div className="grid lg:grid-cols-3 gap-12 lg:gap-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="lg:col-span-2"
              >
                <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] mb-6">
                  {project.description}
                </h2>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-widest mb-2">Client</p>
                  <p className="font-medium">{project.client}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-widest mb-2">Industry</p>
                  <p className="font-medium">{project.industry}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-widest mb-2">Services</p>
                  <div className="flex flex-wrap gap-2">
                    {project.services.map((service) => (
                      <span
                        key={service}
                        className="px-3 py-1 bg-gray-100 text-sm rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="section-padding bg-black text-white">
          <div className="container-custom">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-gray-400 text-sm uppercase tracking-widest mb-12 text-center"
            >
              The Results
            </motion.p>
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {project.results.map((result, index) => (
                <motion.div
                  key={result.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <p className="text-5xl md:text-6xl font-bold font-['Syne'] mb-3">
                    {result.metric}
                  </p>
                  <p className="text-gray-400">{result.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenge & Solution */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <p className="text-gray-500 text-sm uppercase tracking-widest mb-4">
                  The Challenge
                </p>
                <p className="text-xl text-gray-700 leading-relaxed">
                  {project.challenge}
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <p className="text-gray-500 text-sm uppercase tracking-widest mb-4">
                  The Solution
                </p>
                <p className="text-xl text-gray-700 leading-relaxed">
                  {project.solution}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="grid md:grid-cols-3 gap-6">
              {project.gallery.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="aspect-video overflow-hidden"
                >
                  <img
                    src={image}
                    alt={`${project.title} gallery ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        {project.testimonial && (
          <section className="section-padding">
            <div className="container-custom max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <Quote className="w-12 h-12 text-gray-300 mx-auto mb-8" />
                <blockquote className="text-2xl md:text-3xl font-medium font-['Syne'] mb-8 leading-relaxed">
                  "{project.testimonial.quote}"
                </blockquote>
                <div>
                  <p className="font-semibold">{project.testimonial.author}</p>
                  <p className="text-gray-500">{project.testimonial.role}</p>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Navigation */}
        <section className="border-t border-gray-100">
          <div className="grid md:grid-cols-2">
            {prevProject ? (
              <Link
                to={`/work/${prevProject.id}`}
                className="group p-8 md:p-12 hover:bg-gray-50 transition-colors border-r border-gray-100"
              >
                <div className="flex items-center gap-4 text-gray-500 mb-4">
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-2 transition-transform" />
                  <span className="text-sm uppercase tracking-widest">Previous Project</span>
                </div>
                <p className="text-2xl font-bold font-['Syne'] group-hover:text-gray-600 transition-colors">
                  {prevProject.title}
                </p>
              </Link>
            ) : (
              <div className="p-8 md:p-12 border-r border-gray-100" />
            )}
            {nextProject ? (
              <Link
                to={`/work/${nextProject.id}`}
                className="group p-8 md:p-12 hover:bg-gray-50 transition-colors text-right"
              >
                <div className="flex items-center justify-end gap-4 text-gray-500 mb-4">
                  <span className="text-sm uppercase tracking-widest">Next Project</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                </div>
                <p className="text-2xl font-bold font-['Syne'] group-hover:text-gray-600 transition-colors">
                  {nextProject.title}
                </p>
              </Link>
            ) : (
              <Link
                to="/work"
                className="group p-8 md:p-12 hover:bg-gray-50 transition-colors text-right"
              >
                <div className="flex items-center justify-end gap-4 text-gray-500 mb-4">
                  <span className="text-sm uppercase tracking-widest">All Projects</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
                <p className="text-2xl font-bold font-['Syne'] group-hover:text-gray-600 transition-colors">
                  View All Work
                </p>
              </Link>
            )}
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProjectPage;
